// src/components/Projects.js

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Papa from 'papaparse'; // Import PapaParse
import './Projects.css';

// --- NEW: A dedicated Loader component ---
// This component will be shown while data is being fetched.
const Loader = () => {
  return (
    <div className="loader-container">
      <div className="loader-spinner"></div>
    </div>
  );
};

// The ProjectModal component can stay in this file
const ProjectModal = ({ project, onClose, zoomedImage, onImageClick, onCloseZoom }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        zoomedImage ? onCloseZoom() : onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [zoomedImage, onClose, onCloseZoom]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="project-modal" onClick={handleBackdropClick}>
      <div className="modal-content">
        <button className="modal-close-btn" onClick={onClose}>&times;</button>
        <div className="modal-header">
          <h2>{project.title}</h2>
          <span className="category-tag-light">{project.category}</span>
        </div>
        <div className="project-collage-grid">
          {project.images.map((imgUrl, index) => (
            <div key={index} className="collage-image-wrapper" onClick={() => onImageClick(imgUrl)}>
              <img src={imgUrl} alt={`${project.title} - view ${index + 1}`} />
            </div>
          ))}
        </div>
      </div>
      {zoomedImage && (
        <div className="zoomed-image-view" onClick={onCloseZoom}>
          <button className="zoom-close-btn" onClick={onCloseZoom}>&times;</button>
          <img src={zoomedImage} alt="Zoomed view of project" />
        </div>
      )}
    </div>
  );
};

// --- Main Component ---
const Projects = () => {
  const { projectId } = useParams();

  const [projectList, setProjectList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [selectedProject, setSelectedProject] = useState(null);
  const [zoomedImage, setZoomedImage] = useState(null);
  
  useEffect(() => {
    // PASTE YOUR PUBLISHED GOOGLE SHEET CSV URL HERE
    const GOOGLE_SHEET_URL = "https://docs.google.com/spreadsheets/d/e/2PACX-1vS-vnnhMphHYIAAYAuQf9bLzsxMTHH58BYB6op3DGwwfk42Bvd2sus8LTqU7J-U1hdbxsEHxmovFOy2/pub?output=csv";

    Papa.parse(GOOGLE_SHEET_URL, {
        download: true,
        header: true,
        complete: (results) => {
            const validData = results.data.filter(row => row.id && row.id.trim() !== "");
            const transformedData = validData.map(project => ({
                ...project,
                images: project.images ? project.images.split(',').map(img => img.trim()) : []
            }));
            const projectsWithImages = transformedData.filter(
                project => project.images && project.images.length > 0
            );
            setProjectList(projectsWithImages);
            setIsLoading(false);
        },
        error: (err) => {
            console.error("Error fetching project data:", err);
            setError("Could not load projects. Please try again later.");
            setIsLoading(false);
        },
    });
  }, []);

  useEffect(() => {
    if (projectId && projectList.length > 0) {
      const projectToOpen = projectList.find(p => p.id === projectId);
      if (projectToOpen) {
        setSelectedProject(projectToOpen);
      }
    }
  }, [projectId, projectList]);

  const handleProjectClick = (project) => setSelectedProject(project);
  const handleCloseModal = () => {
    setSelectedProject(null);
    setZoomedImage(null);
  };
  const handleImageZoom = (imageUrl) => setZoomedImage(imageUrl);
  const handleCloseZoom = () => setZoomedImage(null);

  // --- MODIFIED: Loading and Error State Handling ---
  if (isLoading) {
      // Return the new Loader component which fills the space
      return <Loader />;
  }

  if (error) {
      // Also wrap the error message in a container to prevent layout collapse
      return (
          <div className="loader-container">
              <p className="projects-status-message error">{error}</p>
          </div>
      );
  }
  
  return (
    <>
      <section className="projects-section-light">
        <div className="section-header-light">
          <h1>Our Work</h1>
          <p>Explore a selection of projects where our solutions have transformed spaces and inspired experiences.</p>
        </div>
        <div className="project-grid-light">
          {projectList.map((project, index) => (
            <div key={project.id} className="project-card-light" style={{ animationDelay: `${index * 100}ms` }}>
              <div className="image-container-light">
                {project.images.length > 0 && (
                  <img src={project.images[0]} alt={project.title} className="project-image-light" />
                )}
              </div>
              <div className="details-panel-light">
                <span className="category-tag-light">{project.category}</span>
                <h2>{project.title}</h2>
                <button onClick={() => handleProjectClick(project)} className="view-project-btn">
                  View Gallery &rarr;
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>
      {selectedProject && (
        <ProjectModal
          project={selectedProject}
          onClose={handleCloseModal}
          zoomedImage={zoomedImage}
          onImageClick={handleImageZoom}
          onCloseZoom={handleCloseZoom}
        />
      )}
    </>
  );
};

export default Projects;